<template>
  <layouts-header></layouts-header>
  <layouts-sidebar></layouts-sidebar>
  <div class="page-wrapper">
    <div class="content">
      <div class="row">
        <div class="col-md-12">
          <page-header :title="title" />
          <settings-tabs></settings-tabs>

          <div class="row">
            <div class="col-xl-12 col-lg-12">
              <div class="card">
                <div class="card-body">
                  <p class="text-muted mb-4">
                    Export all company data as CSV files in a ZIP. The export runs in the background; you can download it when ready or from the list below.
                  </p>
                  <div class="mb-4">
                    <button
                      type="button"
                      class="btn btn-primary"
                      :disabled="exportInProgress"
                      @click="startExport"
                    >
                      <i v-if="exportInProgress" class="ti ti-loader ti-spin me-1"></i>
                      <i v-else class="ti ti-file-export me-1"></i>
                      {{ exportInProgress ? 'Export in progress…' : 'Export company data' }}
                    </button>
                    <p v-if="exportError" class="text-danger mt-2 mb-0">{{ exportError }}</p>
                  </div>

                  <h5 class="mb-3">Recent exports</h5>
                  <div v-if="recentExports.length === 0 && !loadingExports" class="text-muted">
                    No exports yet. Start an export above.
                  </div>
                  <div v-else-if="loadingExports" class="text-muted">
                    Loading…
                  </div>
                  <div v-else class="table-responsive">
                    <table class="table table-hover">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Status</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="exp in recentExports" :key="exp.id">
                          <td>{{ formatDate(exp.created_at) }}</td>
                          <td>
                            <span :class="statusClass(exp.status)">{{ exp.status }}</span>
                            <span v-if="exp.error_message" class="text-danger small d-block">{{ exp.error_message }}</span>
                          </td>
                          <td>
                            <a
                              v-if="exp.status === 'completed' && exp.id"
                              href="javascript:void(0)"
                              class="btn btn-sm btn-outline-primary"
                              @click.prevent="downloadExport(exp)"
                            >
                              <i class="ti ti-download"></i> Download
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Password Modal -->
  <div ref="passwordModalEl" class="modal fade" id="export_password_modal" tabindex="-1" role="dialog"
    aria-labelledby="exportPasswordLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="exportPasswordLabel">
            {{ passwordModalAction === 'startExport' ? 'Start Export' : 'Download Export' }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"
            @click="closePasswordModal"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label">Enter password</label>
            <input
              v-model="passwordModalPassword"
              type="password"
              class="form-control"
              placeholder="Password"
              :disabled="passwordModalLoading"
              @keydown.enter="submitPasswordModal"
            />
          </div>
          <p v-if="passwordModalError" class="text-danger small mb-0">{{ passwordModalError }}</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" @click="closePasswordModal"
            :disabled="passwordModalLoading">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="!passwordModalPassword || passwordModalLoading"
            @click="submitPasswordModal">
            <i v-if="passwordModalLoading" class="ti ti-loader ti-spin me-1"></i>
            {{ passwordModalAction === 'startExport' ? 'Start Export' : 'Download' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '@/axios';
import { ref, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import { Modal } from 'bootstrap';
import { getSessionContext } from '@/utils/session-context';

const POLL_INTERVAL_MS = 3000;
const API_BASE = 'company/export';

export default {
  data() {
    return {
      title: 'Settings',
    };
  },
  setup() {
    const exportInProgress = ref(false);
    const exportError = ref('');
    const recentExports = ref([]);
    const loadingExports = ref(false);

    // Password modal state
    const passwordModalEl = ref(null);
    const passwordModalAction = ref('startExport'); // 'startExport' | 'download'
    const passwordModalPassword = ref('');
    const passwordModalError = ref('');
    const passwordModalLoading = ref(false);
    const selectedExportForDownload = ref(null);

    const formatDate = (val) => {
      if (!val) return '—';
      const d = new Date(val);
      return isNaN(d.getTime()) ? val : d.toLocaleString();
    };

    const statusClass = (status) => {
      if (status === 'completed') return 'text-success';
      if (status === 'failed') return 'text-danger';
      return 'text-warning';
    };

    const fetchRecentExports = async () => {
      loadingExports.value = true;
      exportError.value = '';
      try {
        const res = await apiClient.get(`/${API_BASE}`);
        const data = res.data?.data;
        recentExports.value = Array.isArray(data) ? data : [];
      } catch (err) {
        console.error('Error fetching exports:', err);
        recentExports.value = [];
      } finally {
        loadingExports.value = false;
      }
    };

    const getExportId = (data) => {
      return data?.export_id ?? data?.id;
    };

    const pollStatus = (id) => {
      return new Promise((resolve, reject) => {
        const poll = async () => {
          try {
            const res = await apiClient.get(`/${API_BASE}/${id}/status`, { skipGlobalLoader: true });
            const d = res.data?.data;
            const status = d?.status;
            if (status === 'completed') {
              resolve(d);
              return;
            }
            if (status === 'failed') {
              reject(new Error(d?.error_message || 'Export failed'));
              return;
            }
            setTimeout(poll, POLL_INTERVAL_MS);
          } catch (err) {
            reject(err);
          }
        };
        poll();
      });
    };

    const downloadFilename = (exp) => {
      const id = exp.id || 'export';
      const date = exp.created_at
        ? new Date(exp.created_at).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10);
      return `company_export_${id}_${date}.zip`;
    };

    const triggerDownload = async (exp, password) => {
      const id = exp.id;
      if (!id) {
        message.error('Download not available');
        return;
      }
      const token = localStorage.getItem('authToken');
      if (!token) {
        message.error('Please log in to download.');
        return;
      }
      const baseURL = (apiClient.defaults.baseURL || '').replace(/\/$/, '');
      let downloadUrl = `${baseURL}/${API_BASE}/${id}/download`;
      if (password) {
        downloadUrl += `?password=${encodeURIComponent(password)}`;
      }
      // const subdomain = window.location.host.split('.')[0];
      const { subdomain } = getSessionContext();
      try {
        const response = await fetch(downloadUrl, {
          method: 'GET',
          redirect: 'follow',
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Subdomain': subdomain,
          },
        });
        if (!response.ok) {
          const errText = await response.text();
          let errMsg = 'Download failed';
          try {
            const errJson = JSON.parse(errText);
            if (errJson?.message) errMsg = errJson.message;
          } catch (_) {
            if (errText) errMsg = errText.slice(0, 200);
          }
          throw new Error(errMsg);
        }
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const json = await response.json();
          const urlFromResponse = json?.data?.download_url;
          if (urlFromResponse) {
            window.open(urlFromResponse, '_blank');
            return;
          }
          throw new Error('Download not available');
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = downloadFilename(exp);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (err) {
        throw err;
      }
    };

    const getApiErrorMessage = (err) => {
      return err?.response?.data?.message || err?.message || 'An error occurred';
    };

    const closePasswordModal = () => {
      if (passwordModalEl.value) {
        const modal = Modal.getInstance(passwordModalEl.value);
        if (modal) modal.hide();
      }
      passwordModalPassword.value = '';
      passwordModalError.value = '';
      passwordModalLoading.value = false;
      selectedExportForDownload.value = null;
    };

    const openPasswordModal = (action, exp = null) => {
      passwordModalAction.value = action;
      passwordModalPassword.value = '';
      passwordModalError.value = '';
      passwordModalLoading.value = false;
      selectedExportForDownload.value = exp;
      if (passwordModalEl.value) {
        const modal = new Modal(passwordModalEl.value);
        modal.show();
      }
    };

    const submitPasswordModal = async () => {
      const password = passwordModalPassword.value?.trim();
      if (!password) return;
      passwordModalError.value = '';
      passwordModalLoading.value = true;
      try {
        if (passwordModalAction.value === 'startExport') {
          const token = localStorage.getItem('authToken');
          // const subdomain = window.location.host.split('.')[0];
          const { subdomain } = getSessionContext();
          const baseURL = (apiClient.defaults.baseURL || '').replace(/\/$/, '');
          const postUrl = `${baseURL}/${API_BASE}`;
          const response = await fetch(postUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              'X-Subdomain': subdomain,
            },
            body: JSON.stringify({ password }),
          });
          if (!response.ok) {
            const errText = await response.text();
            let errMsg = 'Export failed';
            try {
              const errJson = JSON.parse(errText);
              if (errJson?.message) errMsg = errJson.message;
            } catch (_) {
              if (errText) errMsg = errText.slice(0, 200);
            }
            throw new Error(errMsg);
          }
          const resData = await response.json();
          const data = resData?.data;
          const id = getExportId(data);
          if (!id) {
            throw new Error('Invalid response: no export id');
          }
          closePasswordModal();
          message.success('Export started. Download will begin when ready.');
          exportError.value = '';
          exportInProgress.value = true;
          const statusData = await pollStatus(id);
          await triggerDownload(statusData, password);
          message.success('Export completed and download started.');
          await fetchRecentExports();
        } else {
          const exp = selectedExportForDownload.value;
          if (!exp?.id) {
            throw new Error('Download not available');
          }
          await triggerDownload(exp, password);
          closePasswordModal();
          message.success('Download started.');
        }
      } catch (err) {
        const msg = getApiErrorMessage(err);
        passwordModalError.value = msg;
        if (passwordModalAction.value === 'startExport') {
          exportInProgress.value = false;
          exportError.value = msg;
        }
        message.error(msg);
      } finally {
        passwordModalLoading.value = false;
      }
    };

    const downloadExport = (exp) => {
      openPasswordModal('download', exp);
    };

    const startExport = () => {
      exportError.value = '';
      openPasswordModal('startExport');
    };

    onMounted(() => {
      fetchRecentExports();
    });

    return {
      exportInProgress,
      exportError,
      recentExports,
      loadingExports,
      formatDate,
      statusClass,
      startExport,
      downloadExport,
      passwordModalEl,
      passwordModalAction,
      passwordModalPassword,
      passwordModalError,
      passwordModalLoading,
      closePasswordModal,
      submitPasswordModal,
    };
  },
};
</script>
